import React, { useState, ChangeEvent, KeyboardEvent, useRef, useEffect } from "react";
import "./Chat.css";
import { httpsCallable } from "firebase/functions";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { addDoc, collection, getDocs, orderBy, query, serverTimestamp } from "firebase/firestore";
import { Avatar } from "@nextui-org/react";
import { useAuth, useFirestore, useFunctions } from "reactfire";

// Type Definitions
interface Message {
    role: "user" | "assistant";
    content: string | any;
    timestamp?: any;
    type?: string;
}

interface QueryResponse {
    answer: string;
}

const Chat: React.FC<{ jobId: string }> = ({ jobId }) => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const functions = useFunctions();
    const firestore = useFirestore();
    const auth = useAuth();
    const userId = auth?.currentUser?.uid;
    const collectionName = `jobs/${jobId}/${userId}`;

    // Save a message to Firestore
    const saveMessageToFirestore = async (message: Message) => {
        try {
            const chatRef = collection(firestore, collectionName);
            await addDoc(chatRef, {
                ...message,
                timestamp: serverTimestamp(), // Add a server-generated timestamp
            });
        } catch (error) {
            console.error("Error saving message to Firestore:", error);
        }
    };

    // Load messages from Firestore on component mount
    useEffect(() => {
        const loadMessages = async () => {
            try {
                const chatRef = collection(firestore, collectionName);
                const q = query(chatRef, orderBy("timestamp", "asc"));
                const querySnapshot = await getDocs(q);

                const loadedMessages: Message[] = querySnapshot.docs.map((doc) => ({
                    role: doc.data().role,
                    content: doc.data().content,
                    timestamp: doc.data().timestamp,
                }));

                setMessages(loadedMessages);
            } catch (error) {
                console.error("Error loading messages:", error);
            }
        };

        loadMessages();
    }, [firestore, jobId, userId]);

    const parseLLMResponse = (response: string): Message => {
        const regex = /```(\w+)\n([\s\S]+?)```/; // Adjusted for triple backticks
        const match = response.match(regex);

        if (match) {
            const [, contentType, content] = match;
            return { role: "assistant", content: content.trim(), type: contentType.trim() };
        }

        // Default case if no type specifier is found
        return { role: "assistant", content: response.trim() };
    };

    const handleSend = async () => {
        if (!input.trim()) return;

        // Add user's message to the chat
        const userMessage: Message = { role: "user", content: input };
        setMessages((prev) => [...prev, userMessage]);

        // Save user's message to Firestore
        await saveMessageToFirestore(userMessage);

        setInput("");
        setIsLoading(true);

        try {
            var fnName = 'api/jobchat/' + jobId;

            const callableFunction = httpsCallable(functions, fnName);
            const response = await callableFunction({ query: userMessage });
            const data = response.data as QueryResponse;
            const answer = data.answer;
            // Add LLM's response to the chat
            // const llmMessage: Message = { role: "assistant", content: answer};
            const llmMessage: Message = parseLLMResponse(answer);
            setMessages((prev) => [...prev, llmMessage]);

            // Save LLM's response to Firestore
            await saveMessageToFirestore(llmMessage);
        } catch (error) {
            // Handle errors gracefully
            const errorMessage: Message = {
                role: "assistant",
                content: "Error fetching results. Please try again.",
            };
            setMessages((prev) => [...prev, errorMessage]);
            // Save error message to Firestore
            await saveMessageToFirestore(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        setInput(e.target.value);
    };

    const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            handleSend();
        }
    };

    // Reference to the chat window for scrolling
    const chatWindowRef = useRef<HTMLDivElement>(null);

    // Scroll to the bottom whenever messages change
    useEffect(() => {
        if (chatWindowRef.current) {
            chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
        }
    }, [messages]);

    return (
        <div className="chat-container max-h-[70vh]">
            <div className="chat-window">
                {messages.map((msg, index) => (

                    msg.role === "assistant" ? (
                        <div
                            key={index}
                            className={`chat-message chat-assistant bg-content3 text-content3-foreground"
                } `}
                        >
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                className=" markdown">
                                {msg.content}
                            </ReactMarkdown>
                        </div>
                    ) : (
                        <div className="flex flex-row gap-2 align-middle">
                            <Avatar src={auth.currentUser?.photoURL || ""} />
                            <div
                                key={index}
                                className={`chat-message chat-user bg-content2 text-default-600"
                        } `}
                            >
                                {msg.content}
                            </div>
                        </div>
                    )

                ))}
                {isLoading && <div className="chat-loading">Loading...</div>}
            </div>
            <div className="chat-input-container">
                <input
                    type="text"
                    className="chat-input"
                    placeholder="Ask a question..."
                    value={input}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                />
                <button className="chat-send-button" onClick={handleSend}>
                    Send
                </button>
            </div>
        </div>
    );
};

export default Chat;